/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type ImageLayer from 'ol/layer/Image';
import { useRef, useState, useEffect, useMemo } from 'react';
import { WMInvalidDateValues } from '@opengeoweb/webmap';
import type { WMLayer } from '@opengeoweb/webmap';
import { ShowLayerInfo } from './ShowLayerInfo';
import type { TimeawareImageSource } from './TimeawareImageSource';

export interface ShowTimeAwareLayerInfoProps {
  wmLayer: WMLayer;
  stepsToCheck: number[];
  layer: ImageLayer<TimeawareImageSource>;
  visible: boolean;
}

export const ShowTimeAwareLayerInfo: React.FC<ShowTimeAwareLayerInfoProps> = ({
  wmLayer,
  stepsToCheck: timeStepsToCheck,
  layer,
  visible,
}: ShowTimeAwareLayerInfoProps) => {
  const wmTimeDim = wmLayer?.dimensions?.find(
    (dimension) => dimension.name === 'time',
  );
  const intervalIdRef = useRef<NodeJS.Timeout>(undefined);
  const [render, setRender] = useState(0);

  // Sets a timer
  useEffect(() => {
    intervalIdRef.current = setInterval(() => {
      setRender(render + 1);
    }, 100);

    return (): void => {
      clearInterval(intervalIdRef.current);
    };
  }, [render]);

  const items = useMemo(() => {
    if (!timeStepsToCheck?.length) {
      return null;
    }
    const itemWidth = Math.round(1000 / timeStepsToCheck.length);
    return timeStepsToCheck.map((timeStep, index) => {
      const closestValue = wmTimeDim?.getClosestValueForTime(timeStep) || '';
      const requestedStepIsOutsideRange = WMInvalidDateValues.has(closestValue);
      const images =
        layer?.getSource()?.getAltGeoReferencedImages(closestValue) || [];

      const isSelectedStep =
        visible && wmTimeDim?.currentValue === closestValue;
      const colorImageIsLoading = layer
        ?.getSource()
        ?.hasImageLoadedForTimeValue(closestValue)
        ? '#40AF50'
        : '#666666';

      const colorImageIsAvailable =
        images?.length > 0 ? colorImageIsLoading : '#FF0000';
      const colorLayerIsOutsideRange = requestedStepIsOutsideRange
        ? '#5080FA'
        : colorImageIsAvailable;
      const height = requestedStepIsOutsideRange ? '15px' : '20px';
      const top = requestedStepIsOutsideRange ? '5px' : '2px';
      const left = `calc(25% + ${index * itemWidth}px)`;
      const opacity = isSelectedStep ? 1 : 0.8;
      return (
        <span
          key={timeStep}
          style={{
            background: isSelectedStep ? '#C0FF50' : colorLayerIsOutsideRange,
            display: 'block',
            position: 'absolute',
            width: `${itemWidth}px`,
            height,
            left,
            top,
            margin: 0,
            padding: 0,
            opacity,
          }}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, timeStepsToCheck, visible, wmTimeDim?.currentValue, render]);

  return (
    <ShowLayerInfo
      dimensions={wmLayer.dimensions}
      key={wmLayer.id}
      title={wmLayer.title}
    >
      <div style={{ width: '100%' }}>
        <div>{items}</div>
      </div>
    </ShowLayerInfo>
  );
};
